import { BaseUserProfile, EnabledFeatures, Gym, IDeeplinkParameters, IEnabledFeatures } from 'fitbeat.models';
import { OSPlatform } from 'fitbeat.models';
import { UserProfile } from '../../entities';

interface IGetHomeLoadingRouteResponse {
  navigationFunction: (route: string) => void;
  route: string;
}

function handleNavigateOnWebReplaceOnApp(
  navigate: (route: string) => void,
  replace: (route: string) => void,
  platform: OSPlatform,
): (route: string) => void {
  if (platform === OSPlatform.web) {
    return navigate;
  }
  return replace;
}

function buildReturn(
  navigationFunction: (route: string) => void,
  route: string,
): IGetHomeLoadingRouteResponse {
  return {
    navigationFunction,
    route,
  };
}

function isEliteOnBoarding(
  deeplinkParameters: IDeeplinkParameters,
  platform: OSPlatform,
) {
  return !!(
    deeplinkParameters.onboarding === 'elite' &&
    deeplinkParameters.planId &&
    deeplinkParameters.pendingPlanId &&
    platform === OSPlatform.web
  );
}

export function getHomeLoadingRouteAndRouter(
  dashboardRoute: string,
  platform: OSPlatform,
  userProfile: UserProfile,
  clearDeepLinkParams: (params: string[]) => void,
  navigate: (route: string) => void,
  replace: (route: string) => void,
  deeplinkParameters: IDeeplinkParameters,
  userGym: Gym | undefined,
  enabledFeatures: IEnabledFeatures,
): IGetHomeLoadingRouteResponse {
  const hasActiveSubscription = BaseUserProfile.hasActivePlan(userProfile);
  const hasAccessToAStudioGym = EnabledFeatures.hasAccessToAStudioGym(enabledFeatures)
  const isHomeOnboarding = !hasAccessToAStudioGym;

  if (deeplinkParameters.route === 'bodyscan') {
    return buildReturn(navigate, 'SummaryDashboard');
  }

  if (deeplinkParameters.route === 'install-app' && platform === 'web') {
    return buildReturn(navigate, 'HomeInstallAppScreen');
  }

  if (deeplinkParameters.route === 'demo-studio-workout-booking') {
    return buildReturn(navigate, 'CreateDemoStudioBooking');
  }

  if (deeplinkParameters.route === 'billing-info') {
    clearDeepLinkParams(['route']);
    return buildReturn(navigate, 'Billing');
  }

  if (deeplinkParameters.invitationCode) {
    // Family plan invite
    return buildReturn(navigate, 'InvitationCodeWelcome');
  }

  if (isHomeOnboarding && isEliteOnBoarding(deeplinkParameters, platform)) {
    return buildReturn(navigate, 'PreLoadedPlanDetails');
  }

  if (deeplinkParameters.route === 'subscription') {
    if (platform === OSPlatform.web) {
      return buildReturn(navigate, 'PreLoadedPlanDetails');
    }
    return buildReturn(replace, 'PaymentFlowNavigator');
  }

  if (deeplinkParameters.influencerId) {
    return buildReturn(navigate, 'InfluencerContainer');
  }

  const hasCompletedOnboarding = !!(userProfile.longTermGoal && userProfile.skillLevel);

  if (isHomeOnboarding && userGym?.defaultHomeSignupFlow) {
    if (platform === OSPlatform.web) {
      if (!hasActiveSubscription && !hasCompletedOnboarding) {
        return buildReturn(handleNavigateOnWebReplaceOnApp(navigate, replace, platform), 'PreLoadedPlanDetails');
      }

      if (!hasCompletedOnboarding) {
        return buildReturn(navigate, 'HomeInstallAppScreen');
      }

      return buildReturn(navigate, dashboardRoute);
    }

    if (!UserProfile.hasCompletedDetailsNeededForHomeUserProfile(userProfile)) {
      return buildReturn(navigate, 'CompleteHomeProfile');
    }

    if (!hasCompletedOnboarding) {
      return buildReturn(navigate, 'HomeOnboardingScreens');
    }

    return buildReturn(navigate, dashboardRoute);
  }

  // User is a studio user from here on
  if (!hasActiveSubscription && !hasCompletedOnboarding && deeplinkParameters.planId) {
    if (platform === OSPlatform.web) {
      return buildReturn(handleNavigateOnWebReplaceOnApp(navigate, replace, platform), 'PreLoadedPlanDetails');
    }
    return buildReturn(handleNavigateOnWebReplaceOnApp(navigate, replace, platform), 'PaymentFlowNavigator');
  }

  if (!hasCompletedOnboarding) {
    if (platform === OSPlatform.web) {
      return buildReturn(navigate, 'HomeInstallAppScreen');
    }
    return buildReturn(navigate, 'HomeOnboardingScreens');
  }

  clearDeepLinkParams(['planId', 'coupon', 'route', 'onceoff', 'referredByFullname', 'referredByUserId']);
  return buildReturn(navigate, dashboardRoute);
}
